//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, Typography } from '@mui/material'
import React, { ReactElement } from 'react'

import { theme } from '@react/components/theme'
import {
  commaDecStringToErc20Wei,
  ERC20DecString,
  ERC20Wei,
  erc20WeiToCommaDecString,
  erc20WeiToErc20DecString,
  toMweiErc20Wei,
  weiToNumber,
} from '@shared/conversions'

import { AddressPill } from '@react/components/addresses/AddressPill'
import OtAdminNumberField from '@react/components/OtAdminNumberField'
import { TableHeaderItem } from '@react/components/TableHeaderItem'
import { useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { NumberFormatEnum } from '@shared/SchemaTypes'
import { DynamicWithdrawalActivityCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'

export default function AdminDynamicRepayRedeemForm(props: {
  activity: DynamicWithdrawalActivityCTO
  symbol: string
  liquidityTokenSymbol: string
  repayWithdrawFn: (args: { activity: DynamicWithdrawalActivityCTO; amount: ERC20Wei; fees: ERC20Wei }) => Promise<void>
}): ReactElement {
  const { activity, symbol, liquidityTokenSymbol, repayWithdrawFn } = props
  const { poolAddr, lender, assets, shares } = activity

  const [amount, setAmount] = React.useState<string>(erc20WeiToErc20DecString(assets))
  const [fees, setFees] = React.useState<string>(erc20WeiToErc20DecString(0))
  const [errorAmount, setErrorAmount] = React.useState<string | undefined>(undefined)
  const [errorFees, setErrorFees] = React.useState<string | undefined>(undefined)
  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const [submitting, setSubmitting] = React.useState(false)
  const [isConfirmed, setIsConfirmed] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    marginLeft: '8px',
    borderRadius: '6px',
    marginTop: 2,
  }

  const errorCheck = (amountBN: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', amountBN)

    if (amountBN === 0n) {
      setErrorAmount('Amount can not be zero')
    } else {
      setErrorAmount(undefined)
    }
  }

  const handleOnChangeAmount = (changedValue: string): void => {
    logOT.debug('handleOnChange', changedValue)

    setAmount(changedValue)
    try {
      const ppt = BigInt(toMweiErc20Wei(changedValue as ERC20DecString))

      errorCheck(ppt)
    } catch (e) {
      logOT.error(e)
    }
  }

  const errorCheckFees = (feesBN: bigint): void => {
    logOT.debug('LenderRequestWithdrawForm SHARES errorCheck', feesBN)

    setErrorFees(undefined)
  }

  const handleOnChangeFees = (changedValue: string): void => {
    logOT.debug('handleOnChange', changedValue)

    setFees(changedValue)
    try {
      const ppt = BigInt(toMweiErc20Wei(changedValue as ERC20DecString))

      errorCheckFees(ppt)
    } catch (e) {
      logOT.error(e)
    }
  }

  const repayEarlyWithdrawAction = async (): Promise<void> => {
    setSubmitting(true)
    try {
      await repayWithdrawFn({
        activity,
        amount: commaDecStringToErc20Wei(amount),
        fees: commaDecStringToErc20Wei(fees),
      })
      const event = new Event('modalClose')
      window.dispatchEvent(event)
    } catch (error) {
      logOT.error('Withdrawal repayment failed:', error)
    } finally {
      setSubmitting(false)
    }
  }
  const disableButton = weiToNumber(poolCTO?.totalAssets) < Number(amount)

  logOT.info('poolCTO?.totalAssets', weiToNumber(poolCTO?.totalAssets), Number(amount), disableButton)
  if (isConfirmed) {
    return (
      <div data-testid="REPAY_EARLY_REDEEM_FORM">
        <Box
          sx={{
            marginTop: 4,
            marginBottom: 2,
            flexDirection: 'column',
            gap: 4,
            display: 'flex',
            alignItems: 'left',
            justifyContent: 'left',
          }}
        >
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Please confirm the repayment of the early withdrawal request.
          </Typography>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Amount to be repaid: {erc20WeiToCommaDecString(commaDecStringToErc20Wei(amount))}{' '}
            {poolCTO.liquidityTokenSymbol}
          </Typography>

          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Fees to be paid: {erc20WeiToCommaDecString(commaDecStringToErc20Wei(fees))}{' '}
            {poolCTO.liquidityTokenSymbol}{' '}
          </Typography>

          <Button
            disabled={submitting || disableButton || Number(amount) <= 0.00001}
            variant={'contained'}
            onClick={() => {
              repayEarlyWithdrawAction()
            }}
          >
            Confirm
          </Button>
        </Box>
      </div>
    )
  }
  return (
    <div data-testid="REPAY_EARLY_REDEEM_FORM">
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            Please set the Early Withdrawal Amount for the Early Withdrawal Request.
          </Typography>
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            This is the amount of {poolCTO.liquidityTokenSymbol} the Lender will receive for this Early Withdrawal
            Request. It should be equal to the Net Sale Proceeds of the Loan Collateral liquidated.
          </Typography>
          <Typography variant="body" sx={{ marginBottom: 4 }}>
            Total Early Withdraw accepted principal {activity.assets} in {poolCTO.liquidityTokenSymbol} Wei{' '}
          </Typography>
        </Grid>
      </Grid>

      {activity && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TableHeaderItem>Lender</TableHeaderItem>
            </Grid>
            <Grid item xs={4}>
              <TableHeaderItem>Shares</TableHeaderItem>
            </Grid>
            <Grid item xs={4}>
              <TableHeaderItem>Asset Value({liquidityTokenSymbol})</TableHeaderItem>
            </Grid>
          </Grid>

          <Grid container sx={{ marginBottom: 4 }}>
            <Grid item xs={4}>
              <Box sx={{ width: '75%' }}>
                <AddressPill addr={lender} />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
                {erc20WeiToCommaDecString(shares)} {symbol}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ marginLeft: 1 }} variant="bodyBold">
                {erc20WeiToCommaDecString(assets)} {liquidityTokenSymbol}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container sx={{ marginTop: 2 }}>
        <Grid item xs={12}>
          <OtAdminNumberField
            id="usdc"
            inputLabel="Repayment Withdrawal Amount"
            value={amount}
            sx={{ width: '300px' }}
            variant="outlined"
            numberFormat={NumberFormatEnum.ERC20}
            onChange={handleOnChangeAmount}
            error={!!errorAmount}
            helperText={errorAmount}
          />
        </Grid>

        <Grid item xs={12}>
          <OtAdminNumberField
            id="usdc"
            inputLabel="Fees"
            value={fees}
            sx={{ width: '300px' }}
            variant="outlined"
            numberFormat={NumberFormatEnum.ERC20}
            onChange={handleOnChangeFees}
            error={!!errorFees}
            helperText={errorFees}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Button
            disabled={disableButton || Number(amount) <= 0.00001}
            sx={sx}
            onClick={() => {
              setIsConfirmed(true)
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Confirm Withdraw Request</Typography>
          </Button>
        </Grid>
        {disableButton && (
          <Typography variant="body" align="left" sx={{ marginBottom: 2 }}>
            The amount you are trying to send for withdraw is greater than the amount of {poolCTO.liquidityTokenSymbol}{' '}
            available in your vault ({weiToNumber(poolCTO?.totalAssets)}). Please deposit funds to the borrowerVault
          </Typography>
        )}
      </Grid>
    </div>
  )
}
